import { reloadOnceGuarded } from "@/components/ErrorBoundary";

/**
 * Resilient code-split chunk loading for iOS Safari.
 *
 * On iOS Safari a dynamic `import()` can transiently abort mid-navigation
 * ("Load failed") and leave a lazy route stuck on a blank Suspense boundary —
 * the buyer taps a sales-page CTA and lands on a blank checkout until they
 * refresh by hand. The old recovery was a global `vite:preloadError` → full page
 * reload, but that has two problems this module fixes:
 *
 *  1. A BACKGROUND prefetch (warmed on idle/hover) that fails would trip the same
 *     reload, blowing the one-shot reload budget before the real click — so when
 *     the click's import then failed, auto-recovery was already spent and the
 *     buyer got the blank checkout anyway.
 *  2. A single transient hiccup forced a whole-page reload when simply retrying
 *     the import in place usually succeeds.
 *
 * So: retry the import a few times in place (recovers the common hiccup with no
 * reload), and while any retry/prefetch is in flight, suppress the global
 * reload. Only a REAL navigation import that still fails after every retry falls
 * back to the one-shot guarded reload; a failed prefetch stays silent.
 */

let suppressDepth = 0;

/** True while a chunk load is retrying — the vite:preloadError handler defers to us. */
export function isChunkReloadSuppressed(): boolean {
  return suppressDepth > 0;
}

// One in-place retry recovers a genuinely transient fetch (the browser refetches
// the module). A hard failure the browser caches won't refetch, so we fall
// through quickly to the guarded reload rather than stalling the buyer.
//
// Measured, so nobody assumes more: in the BUILT app this retry never reaches
// the network. Vite's preload helper memoizes the rejected promise per chunk
// URL, so the second `loader()` re-throws the first failure instantly and the
// only real recovery in production is the reload below. It does refetch under
// the dev server. Raising MAX_RETRIES therefore buys nothing in production
// except delay before the reload — the fix would be a cache-busting import URL,
// which Vite's static analysis of `import()` does not allow.
const MAX_RETRIES = 1;
const RETRY_BASE_MS = 250;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Load a lazy chunk with retry. Pass `prefetch: true` for background warms so a
 * final failure stays silent instead of reloading the page the buyer is reading.
 */
export async function loadChunk<T>(
  loader: () => Promise<T>,
  opts?: { prefetch?: boolean },
): Promise<T> {
  // Suppress the global reload for the whole retry loop, including the awaited
  // back-off: the vite:preloadError from a failed attempt fires while we are
  // still mid-recovery, and reloading then would abort a retry that would have
  // succeeded.
  suppressDepth++;
  try {
    for (let attempt = 0; ; attempt++) {
      try {
        return await loader();
      } catch (err) {
        if (attempt >= MAX_RETRIES) {
          // Real navigation still broken after every retry → last-resort reload
          // (guarded to once per 10s). A background prefetch just gives up; the
          // eventual real navigation will retry and recover on its own.
          if (!opts?.prefetch) reloadOnceGuarded(err);
          throw err;
        }
        await delay(RETRY_BASE_MS * (attempt + 1));
      }
    }
  } finally {
    suppressDepth--;
  }
}
